import { useState } from "react";
import axios from "axios";
import api from "../../services/api";

interface AddCustomerFormProps {
  onCustomerAdded: (customer: any) => void;
  onCancel: () => void;
}

/**
 * Form component for adding a new customer
 */
const AddCustomerForm = ({ onCustomerAdded, onCancel }: AddCustomerFormProps) => {
  const [formData, setFormData] = useState({
    customerNumber: "",
    customerName: "",
    primaryContactEmail: "",
    entraTenantId: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.customerNumber || !formData.customerName) {
      setError("Customer Number and Customer Name are required");
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Prepare data - remove empty strings
      const dataToSubmit = {
        ...formData,
        primaryContactEmail: formData.primaryContactEmail || undefined,
        entraTenantId: formData.entraTenantId || undefined
      };
      
      // Make API request
      const response = await api.post("/customer", dataToSubmit);
      
      // Notify parent component with the newly added customer
      onCustomerAdded({
        ...dataToSubmit,
        customerId: response.data.customerId || 'unknown',
        ...response.data
      });
    } catch (err) {
      console.error("Error adding customer:", err);
      
      // Provide detailed error message
      if (axios.isAxiosError(err) && err.response) {
        setError(`Failed to add customer: ${err.response.status} ${err.response.statusText}. ${err.response.data?.error || ''}`);
      } else {
        setError("Failed to add customer. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card p-lg">
      <h2 className="text-primary mb-md">Add New Customer</h2>
      
      {error && (
        <div className="bg-error p-sm mb-md" style={{ borderRadius: 'var(--border-radius-sm)' }}>
          <p className="text-white">{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-md">
          <label htmlFor="customerNumber">Customer Number *</label>
          <input
            type="text"
            id="customerNumber"
            name="customerNumber"
            value={formData.customerNumber}
            onChange={handleChange}
            required
            className="w-full"
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="mb-md">
          <label htmlFor="customerName">Customer Name *</label>
          <input
            type="text"
            id="customerName"
            name="customerName"
            value={formData.customerName}
            onChange={handleChange}
            required
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="mb-md">
          <label htmlFor="primaryContactEmail">Primary Contact Email</label>
          <input
            type="email"
            id="primaryContactEmail"
            name="primaryContactEmail"
            value={formData.primaryContactEmail}
            onChange={handleChange}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="mb-lg">
          <label htmlFor="entraTenantId">Entra Tenant ID</label>
          <input
            type="text"
            id="entraTenantId"
            name="entraTenantId"
            value={formData.entraTenantId}
            onChange={handleChange}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="flex justify-between">
          <button 
            type="button" 
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner mr-sm" style={{ width: '16px', height: '16px' }}></div>
                Adding...
              </>
            ) : 'Add Customer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCustomerForm;
